import { AuthenticationService } from './../services/authentication.service';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { AppService } from '../services/app.service';
import { sanitizeReturnUrl } from './return-url.util';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private appService: AppService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    // Cheap synchronous gate on the readable session hint. Token expiry is no
    // longer checked here (the JWT is httpOnly and unreadable) — it is enforced
    // server-side: an expired access token yields a 401 that the auth
    // interceptor resolves via refresh, or hard-logout on refresh failure.
    if (!this.authService.isLoggedIn()) {
      return this.denyAndMaybeCaptureRedirect(state);
    }

    // The hint cookie only proves a session was set at some point — it does not
    // prove the access token is still valid (revoked, expired, or a stale/forged
    // cookie value). Confirm the session for real with a lightweight /user/me
    // call. This lives on AuthGuard specifically because it is the first guard
    // in every route's canActivate chain (see app.routes.ts) — RoleGuard,
    // ManageGuard, and setupLockGuard all run after it and read the cached user
    // this call populates, so confirming here once per navigation avoids
    // redundant /user/me calls from every guard on the chain.
    return this.authService.me().pipe(
      map(() => true),
      catchError(() => of(this.denyAndMaybeCaptureRedirect(state)))
    );
  }

  /**
   * Bounce to the login form, remembering where the user was trying to go.
   *
   * The capture used to be limited to `project-overview`, which made every other deep link a silent
   * loss: a PA whose `connect_auth` hint cookie outlives their session is sent here by
   * `loginLandingGuard` with a `/scheduling?visitId=...` tree, fails `me()` on the way in, and lands on
   * the dashboard with no trace of the visit the notification mail was about. Nothing about the route
   * name is what makes a redirect worth keeping, so nothing here tests it.
   *
   * `state.url` is the router's own serialization of a route this app declared — not caller-supplied —
   * but it is written into the same `AppService.redirectUrl` channel the untrusted `?returnUrl=`
   * producer writes to, so it goes through the same sanitizer rather than being trusted for its
   * provenance. A rejected value yields `''`, which must not be stored: it would clobber a target a
   * previous capture had already put there.
   */
  private denyAndMaybeCaptureRedirect(state: RouterStateSnapshot): UrlTree {
    const redirectUrl = sanitizeReturnUrl(state.url);
    if (redirectUrl) {
      this.appService.setRedirectUrl(redirectUrl);
    }
    return this.router.createUrlTree(['/login']);
  }
}
